import type { CSSProperties, ReactNode } from 'react';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DropdownAnchor } from './JourneyPickerDesktop.styles';

const VIEWPORT_MARGIN = 16;
const OFFSET_FROM_TRIGGER = 8;

export function ClampedDropdownAnchor({
  align,
  children,
}: {
  align?: 'center' | 'right';
  children: ReactNode;
}) {
  const placeholderRef = useRef<HTMLSpanElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const compute = useCallback(() => {
    const placeholder = placeholderRef.current;
    const dropdown = dropdownRef.current;
    if (!placeholder || !dropdown || typeof window === 'undefined') return;
    const trigger = placeholder.parentElement;
    if (!trigger) return;

    const triggerRect = trigger.getBoundingClientRect();
    const dropdownRect = dropdown.getBoundingClientRect();

    let left =
      align === 'right'
        ? triggerRect.right - dropdownRect.width
        : triggerRect.left + triggerRect.width / 2 - dropdownRect.width / 2;

    const maxLeft = window.innerWidth - VIEWPORT_MARGIN - dropdownRect.width;
    left = Math.max(VIEWPORT_MARGIN, Math.min(left, maxLeft));

    const top = triggerRect.bottom + OFFSET_FROM_TRIGGER;

    setPos((prev) => (prev && prev.top === top && prev.left === left ? prev : { top, left }));
  }, [align]);

  useLayoutEffect(() => {
    compute();
    const dropdown = dropdownRef.current;
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(compute) : null;
    if (dropdown) observer?.observe(dropdown);
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
    };
  }, [compute]);

  if (typeof document === 'undefined') return null;

  const style: CSSProperties = {
    top: pos?.top ?? 0,
    left: pos?.left ?? 0,
    visibility: pos ? 'visible' : 'hidden',
  };

  return (
    <>
      <span ref={placeholderRef} style={{ display: 'none' }} aria-hidden />
      {createPortal(
        <DropdownAnchor ref={dropdownRef} data-journey-picker-dropdown="true" style={style}>
          {children}
        </DropdownAnchor>,
        document.body
      )}
    </>
  );
}
